// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { Document } from '@shared/data-access/types/engine-types';
import { DocumentScope } from '@shared/data-access/types/engine-types';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getDocumentCardDateLabels } from '../../utils/get-document-card-date-label';
import { useDocumentCatalog } from '../../hooks/use-document-catalog';

export interface UseDocumentsCardListProps {
  readonly data: Document[];
  readonly onViewDetail: (document: Document) => void;
}

const scopeChipColor: Record<DocumentScope, 'default' | 'warning' | 'info'> = {
  [DocumentScope.Personal]: 'default',
  [DocumentScope.Company]: 'warning',
  [DocumentScope.SilverAlpha]: 'info',
};

export const useDocumentsCardList = ({
  data,
  onViewDetail,
}: UseDocumentsCardListProps) => {
  const { t, i18n } = useTranslation();
  const { categoryLabels, subcategoryLabels } = useDocumentCatalog();

  const getClassificationLine = useCallback(
    (document: Document) =>
      [
        categoryLabels[document.category] ?? document.category,
        subcategoryLabels[document.subcategory] ?? document.subcategory,
      ]
        .filter(Boolean)
        .join(' · '),
    [categoryLabels, subcategoryLabels],
  );

  const getSubtitleLine = useCallback(
    (document: Document) => document.issuer ?? '',
    [],
  );

  const getScopeLabel = useCallback(
    (document: Document) =>
      t(`documents.scope.${document.scope}`, { ns: 'components' }),
    [t],
  );

  const getDateLabels = useCallback(
    (document: Document) => getDocumentCardDateLabels(document, t, i18n.language),
    [i18n.language, t],
  );

  const getFileCountLabel = useCallback(
    (document: Document) => {
      const count = document.files?.length ?? 0;

      if (count === 0) {
        return '';
      }

      return t('documents.file-count', { ns: 'components', count });
    },
    [t],
  );

  return {
    t,
    data,
    scopeChipColor,
    getClassificationLine,
    getSubtitleLine,
    getScopeLabel,
    getDateLabels,
    getFileCountLabel,
    onViewDetail,
  };
};
